// Knowledge Base v1.0 PR 4 — typed wrapper over the user-facing Ask /
// Browse / Download endpoints, the admin documents CRUD, the
// category & tag lookup CRUD, the folder-access matrix, and the
// re-index trigger. Errors surface as ApiError per the shared client —
// the Ask 429 is rendered inline with the "limit resets at midnight UTC"
// copy mirroring AnswerAssistantModal.

import { api } from "./client";

export type KbConfidentiality = "CounterpartyFacing" | "InternalOnly";

export type KbDocumentStatus =
  | "Draft"
  | "Processing"
  | "Indexed"
  | "Failed"
  | "Retired";

export type KbTagKind =
  | "Audience"
  | "DocumentType"
  | "ParentAgreement"
  | "Theme"
  | "Lifecycle";

export type KbFeedback = "None" | "Helpful" | "NotHelpful";

export interface KbCitation {
  documentId: string;
  title: string;
  category: string;
  score: number;
}

export interface KbAnswerResult {
  queryId: string;
  answer: string;
  wasAnswered: boolean;
  sources: KbCitation[];
  remainingDailyQuota: number;
}

export interface KbMyAccess {
  accessibleCategoryCodes: string[];
  isManager: boolean;
}

export interface KbDocument {
  id: string;
  title: string;
  description?: string | null;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  blobPath: string;
  fileHashSha256: string;
  primaryCategoryCode: string;
  registryAbbrev?: string | null;
  documentVersion?: string | null;
  effectiveDate?: string | null;
  confidentiality: KbConfidentiality;
  status: KbDocumentStatus;
  indexedAt?: string | null;
  indexError?: string | null;
  uploadedByUserId: string;
  uploadedAt: string;
  updatedAt: string;
  retiredAt?: string | null;
  retiredByUserId?: string | null;
  tagCodes: string[];
}

export interface KbCategory {
  code: string;
  name: string;
  displayOrder: number;
  isSystemReserved: boolean;
}

export interface KbTag {
  code: string;
  label: string;
  kind: KbTagKind;
  displayOrder: number;
}

export interface KbCategoryAccessRow {
  categoryCode: string;
  categoryName: string;
  roleCodes: string[];
}

export interface KbDownloadLink {
  sasUrl: string;
  expiresAt: string;
}

export interface KbReindexResult {
  indexEnsured: boolean;
  indexerRun?: {
    indexerName: string;
    status: string;
    message?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
    itemsProcessed?: number | null;
    itemsFailed?: number | null;
  } | null;
}

export interface KbDocumentListFilter {
  categoryCode?: string | null;
  tagCode?: string | null;
  status?: KbDocumentStatus | null;
  includeRetired?: boolean;
}

export interface KbUploadMetadata {
  title: string;
  description?: string | null;
  primaryCategoryCode: string;
  registryAbbrev?: string | null;
  documentVersion?: string | null;
  effectiveDate?: string | null;
  confidentiality: KbConfidentiality;
  tagCodes: string[];
}

export type KbBulkUploadOutcome =
  | "Created"
  | "SkippedDuplicate"
  | "UncategorizedNeedsReview"
  | "Failed";

export interface KbBulkUploadRow {
  fileName: string;
  relativePath?: string | null;
  outcome: KbBulkUploadOutcome;
  documentId?: string | null;
  inferredCategoryCode?: string | null;
  reason?: string | null;
}

/** Knowledge Base v1.0 PR 5 — the server's bulk-upload summary. One row
 *  per file, plus the four roll-up counts. The single batch indexer run
 *  status is surfaced for the operator runbook. */
export interface KbServerBulkUploadResult {
  createdCount: number;
  skippedDuplicateCount: number;
  uncategorizedNeedsReviewCount: number;
  failedCount: number;
  rows: KbBulkUploadRow[];
  indexerRun?: KbReindexResult["indexerRun"];
}

export interface KbRetireByRegistryResult {
  retiredCount: number;
  retiredDocumentIds: string[];
  unmatchedAbbreviations: string[];
}

export interface KbQueryReviewCitation {
  documentId: string;
  title?: string | null;
  category?: string | null;
  score?: number | null;
}

export interface KbQueryReviewRow {
  id: string;
  userId: string;
  questionText: string;
  answerText: string;
  wasAnswered: boolean;
  topSearchScore?: number | null;
  modelUsed?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  latencyMs?: number | null;
  createdAt: string;
  feedback: KbFeedback;
  feedbackSetAt?: string | null;
  citations: KbQueryReviewCitation[];
}

export interface KbQueryReviewPage {
  rows: KbQueryReviewRow[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// Knowledge Base AI-Assisted Uploader v1.0 PR 2 — frontend types mirroring
// the merged PR-1 KbAiIntakeDtos.cs. The backend does not return a
// `supersedesRecommended` flag; the UI derives it from
// `supersedesConfidence >= 0.70` (matches the server's default
// MinSupersessionConfidence).
export type KbAiIntakeStatus = "Suggested" | "NeedsReview" | "Duplicate" | "Failed";

export interface KbAiIntakeRow {
  suggestionId: string;
  documentId: string | null;
  fileName: string;
  status: KbAiIntakeStatus;
  errorMessage?: string | null;
  suggestedCategoryCode?: string | null;
  suggestedTagCodes: string[];
  suggestedTitle?: string | null;
  suggestedDescription?: string | null;
  confidence?: number | null;
  registryAbbrev?: string | null;
  documentVersion?: string | null;
  suggestedConfidentiality: KbConfidentiality;
  supersedesCandidateDocumentId?: string | null;
  supersedesCandidateTitle?: string | null;
  supersedesConfidence?: number | null;
  supersedesReasoning?: string | null;
}

export interface KbAiIntakeBatch {
  batchId: string;
  rows: KbAiIntakeRow[];
}

export interface KbAiIntakeCommitRow {
  documentId: string;
  categoryCode: string;
  tagCodes: string[];
  title: string;
  description?: string | null;
  confidentiality: KbConfidentiality;
  registryAbbrev?: string | null;
  documentVersion?: string | null;
  effectiveDate?: string | null;
  supersedesDocumentId?: string | null;
}

export type KbAiIntakeCommitOutcome = "Committed" | "Rejected" | "Failed";

export interface KbAiIntakeCommitRowResult {
  documentId: string;
  outcome: KbAiIntakeCommitOutcome;
  supersededDocumentId?: string | null;
  errorMessage?: string | null;
}

export interface KbAiIntakeCommitResult {
  batchId: string;
  committedCount: number;
  rejectedCount: number;
  failedCount: number;
  supersededCount: number;
  rows: KbAiIntakeCommitRowResult[];
  indexerRun?: KbReindexResult["indexerRun"];
}

// --- Admin: AI-assisted intake — accuracy view (Knowledge Base AI-Assisted Uploader v1.0 PR 3) --

export interface KbAccuracyVolumeStats {
  totalClassified: number;
  totalCommitted: number;
  totalAbandoned: number;
  distinctBatches: number;
}

export interface KbAccuracyCorrectionPair {
  suggestedCategoryCode: string;
  acceptedCategoryCode: string;
  count: number;
}

export interface KbAccuracyCategoryStats {
  sampleCount: number;
  accuracyRate: number | null;
  topCorrections: KbAccuracyCorrectionPair[];
}

export interface KbAccuracyTagStats {
  sampleCount: number;
  precision: number | null;
  recall: number | null;
  f1: number | null;
  exactSetMatchRate: number | null;
}

export interface KbAccuracyTextStats {
  sampleCount: number;
  keptVerbatimRate: number | null;
}

export interface KbAccuracySupersessionStats {
  suggestedCount: number;
  acceptedCount: number;
  correctCount: number;
  precision: number | null;
  recall: number | null;
}

export interface KbAccuracyConfidenceCalibration {
  correctSampleCount: number;
  correctedSampleCount: number;
  meanConfidenceWhenCorrect: number | null;
  meanConfidenceWhenCorrected: number | null;
}

export interface KbAccuracyCostStats {
  rowCount: number;
  totalEstimatedCostUsd: number;
  meanCostUsdPerDoc: number | null;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface KbAccuracyRecentSample {
  suggestionId: string;
  knowledgeBaseDocumentId?: string | null;
  fileName?: string | null;
  documentTitle?: string | null;
  acceptedAt: string;
  suggestedCategoryCode?: string | null;
  acceptedCategoryCode?: string | null;
  suggestedConfidence?: number | null;
  categoryChanged: boolean;
  tagsChanged: boolean;
  titleChanged: boolean;
  descriptionChanged: boolean;
  supersessionChanged: boolean;
}

export interface KbClassificationAccuracy {
  filterDays: number | null;
  generatedAtUtc: string;
  volume: KbAccuracyVolumeStats;
  category: KbAccuracyCategoryStats;
  tags: KbAccuracyTagStats;
  title: KbAccuracyTextStats;
  description: KbAccuracyTextStats;
  supersession: KbAccuracySupersessionStats;
  confidenceCalibration: KbAccuracyConfidenceCalibration;
  cost: KbAccuracyCostStats;
  recentSamples: KbAccuracyRecentSample[];
}

const BASE = "/knowledge-base";

export const knowledgeBaseApi = {
  // --- Ask (user-facing) -----------------------------------------------
  ask: (question: string, categoryFilter?: string | null) =>
    api.post<KbAnswerResult>(`${BASE}/ask`, {
      question,
      categoryFilter: categoryFilter ?? null,
    }),

  submitFeedback: (queryId: string, feedback: "Helpful" | "NotHelpful") =>
    api.post<void>(`${BASE}/queries/${queryId}/feedback`, { feedback }),

  // --- Browse + my-access (gated library) ------------------------------
  myAccess: () => api.get<KbMyAccess>(`${BASE}/my-access`),

  browseLibrary: (filter?: {
    categoryCode?: string;
    tagCode?: string;
    search?: string;
  }) =>
    api.get<KbDocument[]>(`${BASE}/library`, {
      categoryCode: filter?.categoryCode,
      tagCode: filter?.tagCode,
      search: filter?.search,
    }),

  /** Returns a fresh SAS URL for the document if the caller's folder
   *  access permits; 403 otherwise. */
  getDownloadLink: (documentId: string) =>
    api.get<KbDownloadLink>(`${BASE}/library/${documentId}/download`),

  // --- Admin: documents CRUD ------------------------------------------
  listDocuments: (filter?: KbDocumentListFilter) =>
    api.get<KbDocument[]>(`${BASE}/documents`, {
      categoryCode: filter?.categoryCode ?? undefined,
      tagCode: filter?.tagCode ?? undefined,
      status: filter?.status ?? undefined,
      includeRetired: filter?.includeRetired ?? false,
    }),

  getDocument: (id: string) => api.get<KbDocument>(`${BASE}/documents/${id}`),

  uploadDocument: (file: File, metadata: KbUploadMetadata) => {
    const form = new FormData();
    form.append("title", metadata.title);
    if (metadata.description) form.append("description", metadata.description);
    form.append("primaryCategoryCode", metadata.primaryCategoryCode);
    if (metadata.registryAbbrev) form.append("registryAbbrev", metadata.registryAbbrev);
    if (metadata.documentVersion) form.append("documentVersion", metadata.documentVersion);
    if (metadata.effectiveDate) form.append("effectiveDate", metadata.effectiveDate);
    form.append("confidentiality", metadata.confidentiality);
    if (metadata.tagCodes.length > 0)
      form.append("tagCodesCsv", metadata.tagCodes.join(","));
    form.append("file", file);
    return api.upload<KbDocument>(`${BASE}/documents`, form);
  },

  /**
   * Knowledge Base v1.0 PR 5 — folder-aware bulk upload. Posts every file
   * in a single multipart request alongside a parallel <c>relativePaths</c>
   * field so the server can infer <c>PrimaryCategoryCode</c> from the
   * top-level folder name. ONE indexer run is triggered server-side after
   * the batch — never one per file.
   */
  bulkUploadDocuments: (
    items: { file: File; relativePath?: string }[],
  ): Promise<KbServerBulkUploadResult> => {
    const form = new FormData();
    for (const it of items) {
      form.append("files", it.file);
      form.append("relativePaths", it.relativePath ?? it.file.name);
    }
    return api.upload<KbServerBulkUploadResult>(`${BASE}/documents/bulk`, form);
  },

  // --- Admin: AI-assisted intake (Knowledge Base AI-Assisted Uploader v1.0 PR 2) -------
  /**
   * Classifies every file via Claude and persists a Draft per file. Sync,
   * hard-capped server-side at KbClassificationOptions.MaxFilesPerBatch
   * (25). The browser fetch has no built-in timeout (see client.ts), so the
   * call can take as long as the server needs to process the batch.
   */
  aiIntake: (files: File[]): Promise<KbAiIntakeBatch> => {
    const form = new FormData();
    for (const f of files) form.append("files", f);
    return api.upload<KbAiIntakeBatch>(`${BASE}/documents/ai-intake`, form);
  },

  getAiIntakeBatch: (batchId: string) =>
    api.get<KbAiIntakeBatch>(`${BASE}/documents/ai-intake/${batchId}`),

  /** Commits the rows the admin kept. Drafts whose DocumentId is omitted
   *  from `rows` are rejected (deleted) server-side. */
  commitAiIntake: (batchId: string, rows: KbAiIntakeCommitRow[]) =>
    api.post<KbAiIntakeCommitResult>(
      `${BASE}/documents/ai-intake/${batchId}/commit`,
      { rows },
    ),

  /** Read-only accuracy snapshot (Knowledge Base AI-Assisted Uploader
   *  v1.0 PR 3). `days` is optional; omit for all-time. */
  getClassificationAccuracy: (days?: number) =>
    api.get<KbClassificationAccuracy>(
      `${BASE}/documents/ai-intake/accuracy`,
      days ? { days } : undefined,
    ),

  retireByRegistry: (abbreviations: string[]) =>
    api.post<KbRetireByRegistryResult>(`${BASE}/documents/retire-by-registry`, {
      abbreviations,
    }),

  listAdminQueries: (filter?: {
    feedback?: KbFeedback;
    wasAnswered?: boolean;
    page?: number;
    pageSize?: number;
  }) =>
    api.get<KbQueryReviewPage>(`${BASE}/admin/queries`, {
      feedback: filter?.feedback,
      wasAnswered: filter?.wasAnswered,
      page: filter?.page ?? 1,
      pageSize: filter?.pageSize ?? 25,
    }),

  updateDocumentMetadata: (id: string, metadata: KbUploadMetadata) =>
    api.put<KbDocument>(`${BASE}/documents/${id}`, metadata),

  replaceDocumentFile: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.upload<KbDocument>(`${BASE}/documents/${id}/file`, form);
  },

  retireDocument: (id: string) =>
    api.post<KbDocument>(`${BASE}/documents/${id}/retire`),

  restoreDocument: (id: string) =>
    api.post<KbDocument>(`${BASE}/documents/${id}/restore`),

  reindex: () => api.post<KbReindexResult>(`${BASE}/reindex`),

  // --- Admin: categories ----------------------------------------------
  listCategories: () => api.get<KbCategory[]>(`${BASE}/categories`),

  createCategory: (body: {
    code: string;
    name: string;
    displayOrder: number;
  }) => api.post<KbCategory>(`${BASE}/categories`, body),

  updateCategory: (
    code: string,
    body: { name: string; displayOrder: number },
  ) => api.put<KbCategory>(`${BASE}/categories/${encodeURIComponent(code)}`, body),

  deleteCategory: (code: string) =>
    api.delete<void>(`${BASE}/categories/${encodeURIComponent(code)}`),

  // --- Admin: tags ----------------------------------------------------
  listTags: (kind?: KbTagKind) =>
    api.get<KbTag[]>(`${BASE}/tags`, kind ? { kind } : undefined),

  createTag: (body: {
    code: string;
    label: string;
    kind: KbTagKind;
    displayOrder: number;
  }) => api.post<KbTag>(`${BASE}/tags`, body),

  updateTag: (
    code: string,
    body: { label: string; kind: KbTagKind; displayOrder: number },
  ) => api.put<KbTag>(`${BASE}/tags/${encodeURIComponent(code)}`, body),

  deleteTag: (code: string) =>
    api.delete<void>(`${BASE}/tags/${encodeURIComponent(code)}`),

  // --- Admin: folder-access matrix ------------------------------------
  getCategoryAccessMatrix: () =>
    api.get<KbCategoryAccessRow[]>(`${BASE}/category-access`),

  setCategoryAccess: (categoryCode: string, roleCodes: string[]) =>
    api.put<KbCategoryAccessRow>(
      `${BASE}/category-access/${encodeURIComponent(categoryCode)}`,
      { roleCodes },
    ),
};
